import React, { useEffect, useMemo, useState } from 'react';
import {
  getCompactFeatureStringsForClubTier,
  getCompactPlanCardFeatures,
  getRegionFromCountryCode,
  normalizeClubPlanTier,
  resolveSubscriptionPlanPrice,
  COMBINED_BUNDLE_DISCOUNT_PERCENT,
  calculateCombinedBundleDiscount,
  calculateCombinedBundleSubtotal,
  calculateCombinedBundleTotal,
  type ClubPlanRecommendationResult,
  type ClubPlanTier,
  type Plan,
  type PlanRecommendationResult,
  type SubscriptionTier,
} from '@court-sync/shared';
import {
  Check,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Crown,
  Loader2,
  ShieldCheck,
  Star,
  Zap,
} from 'lucide-react';
import { Card } from '@/components/ui';
import { subscriptionService } from '@/services/subscription';
import regionService from '@/services/regionService';
import {
  formatCurrency,
  getCurrencyFromRegion,
  getCurrencySymbol,
  type Currency,
} from '@/utils/currency';
import {
  ACADEMY_BUNDLE_TIERS,
  CLUB_TIER_FOR_ACADEMY_BUNDLE,
  formatClubPlanTierLabel,
  formatPlanTierLabel,
  findClubPlanByTier,
  findPlanByTier,
} from '@/utils/planRecommendationUi';
import {
  persistBillingInterval,
  readStoredBillingInterval,
  resolveInitialBillingInterval,
} from '@/utils/billingInterval';
import { CombinedPlanRecommendationBanner } from './CombinedPlanRecommendationBanner';

const COMPACT_FEATURE_PREVIEW_COUNT = 5;

function getAcademyTierIcon(tier: SubscriptionTier) {
  if (tier === 'starter') return <Star className="h-6 w-6" />;
  if (tier === 'professional') return <Zap className="h-6 w-6" />;
  if (tier === 'enterprise') return <ShieldCheck className="h-6 w-6" />;
  return <Crown className="h-6 w-6" />;
}

export interface CombinedOnboardingPlanSelectionProps {
  academyRecommendation: PlanRecommendationResult;
  clubRecommendation: ClubPlanRecommendationResult;
  selectedAcademyTier: SubscriptionTier;
  selectedClubTier: ClubPlanTier;
  onSelectBundle?: (academyTier: SubscriptionTier, clubTier: ClubPlanTier) => void;
  marketCountry?: string | null;
}

export function CombinedOnboardingPlanSelection({
  academyRecommendation,
  clubRecommendation,
  selectedAcademyTier,
  selectedClubTier: _selectedClubTier,
  onSelectBundle,
  marketCountry,
}: CombinedOnboardingPlanSelectionProps) {
  const [academyPlans, setAcademyPlans] = useState<Plan[]>([]);
  const [clubPlans, setClubPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>(() =>
    resolveInitialBillingInterval(null, readStoredBillingInterval())
  );
  const [expandedBundles, setExpandedBundles] = useState<Set<SubscriptionTier>>(new Set());

  const currency = useMemo<Currency>(() => {
    const region = getRegionFromCountryCode(marketCountry);
    return getCurrencyFromRegion(region);
  }, [marketCountry]);

  useEffect(() => {
    let cancelled = false;

    async function loadPlans() {
      try {
        setLoading(true);
        setError(null);

        let region = getRegionFromCountryCode(marketCountry);
        if (!marketCountry) {
          try {
            region = await regionService.detectRegion();
          } catch {
            // keep country-derived or default region
          }
        }

        const [fetchedAcademyPlans, fetchedClubPlans] = await Promise.all([
          subscriptionService.getPlans(region, 'academy'),
          subscriptionService.getClubPlans(region),
        ]);

        if (!cancelled) {
          setAcademyPlans(
            fetchedAcademyPlans.filter((plan) => !normalizeClubPlanTier(plan.internalId ?? null)),
          );
          setClubPlans(fetchedClubPlans);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load plans');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadPlans();
    return () => {
      cancelled = true;
    };
  }, [marketCountry]);

  const handleBillingIntervalChange = (interval: 'month' | 'year') => {
    setBillingInterval(interval);
    persistBillingInterval(interval);
  };

  const getPlanPrice = (plan: Plan | undefined, interval: 'month' | 'year'): number => {
    if (!plan) return 0;
    const resolved = resolveSubscriptionPlanPrice(plan, {
      currency,
      interval,
    });
    return resolved?.amountMajor ?? 0;
  };

  const formatCombinedPrice = (
    academyPlan: Plan | undefined,
    clubPlan: Plan | undefined,
    interval: 'month' | 'year',
  ): string => {
    const subtotal = getPlanPrice(academyPlan, interval) + getPlanPrice(clubPlan, interval);
    const total = calculateCombinedBundleTotal(subtotal);
    return formatCurrency(total, currency);
  };

  const formatCombinedSubtotal = (
    academyPlan: Plan | undefined,
    clubPlan: Plan | undefined,
    interval: 'month' | 'year',
  ): string => {
    const subtotal = getPlanPrice(academyPlan, interval) + getPlanPrice(clubPlan, interval);
    return formatCurrency(subtotal, currency);
  };

  const formatCombinedDiscount = (
    academyPlan: Plan | undefined,
    clubPlan: Plan | undefined,
    interval: 'month' | 'year',
  ): string => {
    const subtotal = calculateCombinedBundleSubtotal(
      getPlanPrice(academyPlan, interval),
      getPlanPrice(clubPlan, interval),
    );
    return formatCurrency(calculateCombinedBundleDiscount(subtotal), currency);
  };

  const formatRoundedMonthlyFromYearly = (
    academyPlan: Plan | undefined,
    clubPlan: Plan | undefined,
  ): string => {
    const yearlySubtotal = getPlanPrice(academyPlan, 'year') + getPlanPrice(clubPlan, 'year');
    const yearlyTotal = calculateCombinedBundleTotal(yearlySubtotal);
    return `${getCurrencySymbol(currency)}${Math.round(yearlyTotal / 12)}`;
  };

  const recommendedAcademyTier = academyRecommendation.recommendedTier;

  if (loading) {
    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" aria-hidden />
        <p className="text-sm text-slate-600 dark:text-slate-400">Loading plans…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <CombinedPlanRecommendationBanner
        academyRecommendation={academyRecommendation}
        clubRecommendation={clubRecommendation}
      />

      <p className="text-sm text-slate-600 dark:text-slate-400">
        Each bundle includes one academy plan and one club plan on a single subscription. Save{' '}
        {COMBINED_BUNDLE_DISCOUNT_PERCENT}% on the combined total. One 14-day free trial applies to
        checkout.
      </p>

      <div className="flex justify-center sm:justify-start">
        <div
          className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50/90 p-1 shadow-sm dark:border-slate-600 dark:bg-slate-900/50"
          role="group"
          aria-label="Billing period"
        >
          <button
            type="button"
            onClick={() => handleBillingIntervalChange('month')}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
              billingInterval === 'month'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white'
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => handleBillingIntervalChange('year')}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
              billingInterval === 'year'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white'
            }`}
          >
            Yearly
            <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
              Save 20%
            </span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {ACADEMY_BUNDLE_TIERS.map((academyTier) => {
          const clubTier = CLUB_TIER_FOR_ACADEMY_BUNDLE[academyTier];
          const academyPlan = findPlanByTier(academyPlans, academyTier);
          const clubPlan = findClubPlanByTier(clubPlans, clubTier);
          const isSelected = selectedAcademyTier === academyTier;
          const isRecommended = academyTier === recommendedAcademyTier;

          const academyFeatures = academyPlan
            ? getCompactPlanCardFeatures({
                name: academyPlan.name,
                internalId: academyPlan.internalId ?? null,
              })
            : [];
          const clubFeatures = getCompactFeatureStringsForClubTier(clubTier);
          const allFeatures = [
            ...academyFeatures.map((feature) => `Academy: ${feature}`),
            ...clubFeatures.map((feature) => `Club: ${feature}`),
          ];
          const isExpanded = expandedBundles.has(academyTier);
          const visibleFeatures = isExpanded
            ? allFeatures
            : allFeatures.slice(0, COMPACT_FEATURE_PREVIEW_COUNT);
          const hasMoreFeatures = allFeatures.length > COMPACT_FEATURE_PREVIEW_COUNT;

          return (
            <Card
              key={academyTier}
              className={`relative overflow-visible border-slate-200/90 transition-shadow duration-200 hover:shadow-md dark:border-slate-700 ${
                isSelected || isRecommended
                  ? 'border-primary-300 shadow-md ring-2 ring-primary-500/25 dark:border-primary-600 dark:ring-primary-400/20'
                  : ''
              }`}
            >
              {isRecommended ? (
                <div className="pointer-events-none absolute -top-3 left-4 z-10">
                  <span className="whitespace-nowrap rounded-full bg-primary-600 px-3 py-1 text-xs font-semibold text-white shadow-md">
                    Recommended
                  </span>
                </div>
              ) : null}

              <div className="flex w-full flex-col p-5 pt-7 sm:flex-row sm:items-start sm:gap-5">
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    className="flex w-full items-start gap-4 text-left"
                    onClick={() => onSelectBundle?.(academyTier, clubTier)}
                    aria-pressed={isSelected}
                  >
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600 dark:bg-primary-900/60 dark:text-primary-400">
                      {getAcademyTierIcon(academyTier)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                        {formatPlanTierLabel(academyTier)} bundle
                      </h3>
                      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                        {formatPlanTierLabel(academyTier)} academy + {formatClubPlanTierLabel(clubTier)}{' '}
                        club management
                      </p>
                    </div>
                  </button>
                  <ul
                    className="mt-3 space-y-1 ml-16"
                    aria-label={`${formatPlanTierLabel(academyTier)} bundle features`}
                  >
                    {visibleFeatures.map((feature) => (
                      <li
                        key={feature}
                        className="flex gap-2 text-xs leading-snug text-slate-600 dark:text-slate-300"
                      >
                        <CheckCircle
                          className="mt-px h-3.5 w-3.5 shrink-0 text-emerald-500"
                          aria-hidden
                        />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {hasMoreFeatures ? (
                    <button
                      type="button"
                      className="mt-2 ml-16 inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                      aria-expanded={isExpanded}
                      onClick={() => {
                        setExpandedBundles((previous) => {
                          const next = new Set(previous);
                          if (next.has(academyTier)) {
                            next.delete(academyTier);
                          } else {
                            next.add(academyTier);
                          }
                          return next;
                        });
                      }}
                    >
                      {isExpanded ? (
                        <>
                          Show less
                          <ChevronUp className="h-3.5 w-3.5" aria-hidden />
                        </>
                      ) : (
                        <>
                          Show all ({allFeatures.length})
                          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                        </>
                      )}
                    </button>
                  ) : null}
                </div>

                <button
                  type="button"
                  className="mt-4 shrink-0 text-left sm:mt-0 sm:text-right"
                  onClick={() => onSelectBundle?.(academyTier, clubTier)}
                  aria-pressed={isSelected}
                >
                  {billingInterval === 'month' ? (
                    <>
                      <p className="text-sm text-slate-500 line-through dark:text-slate-400">
                        {formatCombinedSubtotal(academyPlan, clubPlan, 'month')}
                      </p>
                      <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                        Bundle discount ({COMBINED_BUNDLE_DISCOUNT_PERCENT}%): −
                        {formatCombinedDiscount(academyPlan, clubPlan, 'month')}
                      </p>
                      <p className="text-2xl font-bold tabular-nums text-slate-900 dark:text-white">
                        {formatCombinedPrice(academyPlan, clubPlan, 'month')}
                        <span className="text-base font-semibold text-slate-500 dark:text-slate-400">
                          /mo
                        </span>
                      </p>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Combined price after 14-day free trial
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-slate-500 line-through dark:text-slate-400">
                        {getCurrencySymbol(currency)}
                        {Math.round(
                          (getPlanPrice(academyPlan, 'year') + getPlanPrice(clubPlan, 'year')) / 12,
                        )}
                        /mo
                      </p>
                      <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                        Bundle discount ({COMBINED_BUNDLE_DISCOUNT_PERCENT}%): −
                        {formatCombinedDiscount(academyPlan, clubPlan, 'year')} /yr
                      </p>
                      <p className="text-2xl font-bold tabular-nums text-slate-900 dark:text-white">
                        {formatRoundedMonthlyFromYearly(academyPlan, clubPlan)}
                        <span className="text-base font-semibold text-slate-500 dark:text-slate-400">
                          /mo
                        </span>
                      </p>
                      <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                        Billed annually ({formatCombinedPrice(academyPlan, clubPlan, 'year')})
                      </p>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Combined price after 14-day free trial
                      </p>
                    </>
                  )}
                  <div className="mt-3 flex justify-end">
                    <span
                      className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${
                        isSelected
                          ? 'border-primary-600 bg-primary-600 text-white dark:border-primary-400 dark:bg-primary-500'
                          : 'border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-900'
                      }`}
                      aria-hidden
                    >
                      {isSelected ? <Check className="h-3 w-3" /> : null}
                    </span>
                  </div>
                </button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
